import React, { useState } from 'react';
import { X, Download, Mail, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import emailjs from '@emailjs/browser';

interface EmailCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  downloadUrl: string;
  downloadName?: string;
  source: string;
  promptId?: string;
}

export default function EmailCaptureModal({
  isOpen,
  onClose,
  title = "Download Resource",
  description = "Please enter your email to receive this resource.",
  downloadUrl,
  promptId,
  source
}: EmailCaptureModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Split the name into first and last name
      const nameParts = name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Store email in Supabase CRM contacts
      const { error: contactError } = await supabase
        .from('crm_contacts')
        .insert([{
          first_name: firstName,
          last_name: lastName,
          email: email,
          source: source,
          status: 'prospect',
          last_contact_date: new Date().toISOString()
        }]);

      if (contactError) throw contactError;

      // If this is a prompt download, track it
      if (promptId) {
        const { error: downloadError } = await supabase
          .from('prompt_downloads')
          .insert([{
            prompt_id: promptId,
            name: name,
            email: email,
            source: source
          }]);

        if (downloadError) {
          console.error('Error tracking prompt download:', downloadError);
          // Continue even if tracking fails
        }
      }

      // Set the PDF URL for the success message
      setPdfUrl(downloadUrl);
      
      // Prepare data for the notification email
      const emailData = {
        to_email: 'leads-aaaahdgij3xgdwhoffnd4fd7uu@one80workspace.slack.com',
        from_name: name || 'Website Visitor',
        from_email: email,
        subject: `Resource Downloaded: ${source}`,
        message: `A resource was downloaded:\n\nName: ${name || 'Not provided'}\nEmail: ${email}\nResource: ${source}\nDate: ${new Date().toLocaleString()}`
      };
      
      // Send notification email to Slack using the prompt download template
      try {
        // Use the specific prompt template ID from environment variables
        const templateId = import.meta.env.VITE_EMAILJS_PROMPT_TEMPLATE_ID;
        if (!templateId) {
          console.warn('Prompt template ID not found, falling back to default template');
        }
        
        await emailjs.send(
          import.meta.env.VITE_EMAILJS_SERVICE_ID,
          templateId || import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
          emailData,
          import.meta.env.VITE_EMAILJS_PUBLIC_KEY
        );
        console.log('Download notification email sent to Slack');
      } catch (emailError) {
        console.error('Error sending download notification email:', emailError);
        // Continue even if email fails
      }
      
      // Open the PDF in a new tab
      window.open(downloadUrl, '_blank');

      setSuccess(true);
      
      // Close modal after a delay
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setEmail('');
        setName('');
        setPdfUrl(null);
      }, 3000);
    } catch (err) {
      console.error('Error storing email:', err);
      setError('Failed to process your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <div className="text-center py-6">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Thank You!</h3>
              <p className="text-gray-600">Your PDF is opening in a new tab.</p>
              {pdfUrl && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500 mb-2">If it doesn't open automatically, please click below:</p>
                  <a 
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c] transition-colors duration-200"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    Open PDF
                  </a>
                </div>
              )}
            </div>
          ) : (
            <>
              <p className="text-gray-600 mb-6">{description}</p>
              
              {error && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                    placeholder="you@example.com"
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F98B3D] disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader className="animate-spin h-5 w-5 mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Download className="h-5 w-5 mr-2" />
                      Download Resource
                    </>
                  )}
                </button>
                
                <p className="text-xs text-gray-500 text-center mt-4">
                  By submitting this form, you agree to receive occasional updates about our services.
                  We respect your privacy and will never share your information.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}